import { Sucursal } from '../../types/stores';

export const sortStoresByDistance = (
  stores: Sucursal[],
  userLocation: { lat: number; lng: number }
): Sucursal[] => {
  return stores
    .map((store) => ({
      ...store,
      distance: calculateDistance(
        userLocation.lat,
        userLocation.lng,
        store.coordinates.lat,
        store.coordinates.lng
      ),
    }))
    .sort((a, b) => (a.distance || 0) - (b.distance || 0));
};

export const calculateDistance = (
  lat1: number,
  lng1: number,
  lat2: number,
  lng2: number
): number => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLng = ((lng2 - lng1) * Math.PI) / 180;
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

export const formatDistance = (distance: number): string => {
  if (distance < 1) {
    return `${Math.round(distance * 1000)} metros`;
  }
  return `${distance.toFixed(1)} kilometros`;
};

export const isMobileOrTablet = (): boolean => {
  if (typeof window === 'undefined') return false;
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
    navigator.userAgent
  );
};

// Funciones para manejo de navegación
export const openInWaze = (
  lat: number,
  lng: number,
  storeName: string
): void => {
  // const wazeUrl = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
  const wazeAppUrl = `waze://?ll=${lat},${lng}&navigate=yes`;
  const wazeWebUrl = `https://waze.com/ul?ll=${lat},${lng}&navigate=yes`;
  const googleMapsUrl = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;

  // Intentar abrir Waze
  try {
    if (typeof window === 'undefined') return;
    window.location = wazeAppUrl as unknown as string & Location;
    setTimeout(() => {
      // Si no abrió la app, después de 1s abre el navegador
      window.location = wazeWebUrl as unknown as string & Location;
    }, 1000);

    // Mostrar fallback después de un delay
    setTimeout(() => {
      showWazeFallback(storeName, wazeWebUrl);
    }, 1500);
  } catch (error) {
    showWazeFallback(storeName, googleMapsUrl);
  }
};

export const openInGoogleMaps = (lat: number, lng: number): void => {
  const url = `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
  window.open(url, '_blank');
};

const showWazeFallback = (storeName: string, googleMapsUrl: string): void => {
  // Crear un modal más elegante en lugar de usar confirm
  const modal = document.createElement('div');
  modal.className =
    'fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4';
  modal.innerHTML = `
    <div class="bg-white rounded-lg p-6 max-w-sm w-full">
      <div class="text-center">
        <div class="mb-4">
          <div class="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-3">
            <svg class="w-6 h-6 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"></path>
            </svg>
          </div>
          <h3 class="text-lg font-bold text-gray-900 mb-2">Waze no disponible</h3>
          <p class="text-sm text-gray-600 mb-4">
            No se pudo abrir Waze. ¿Quieres abrir en Google Maps en su lugar?
          </p>
          <p class="text-xs text-gray-500 mb-4">${storeName}</p>
        </div>
        <div class="flex space-x-3">
          <button id="open-google-maps" class="flex-1 bg-appPrimary-500 hover:bg-blue-700 text-white py-2 px-4 rounded-lg font-medium transition-colors text-sm">
            Abrir Google Maps
          </button>
          <button id="cancel-navigation" class="flex-1 bg-gray-200 hover:bg-gray-300 text-gray-800 py-2 px-4 rounded-lg font-medium transition-colors text-sm">
            Cancelar
          </button>
        </div>
      </div>
    </div>
  `;

  document.body.appendChild(modal);

  // Event listeners
  modal.querySelector('#open-google-maps')?.addEventListener('click', () => {
    window.open(googleMapsUrl, '_blank');
    document.body.removeChild(modal);
  });

  modal.querySelector('#cancel-navigation')?.addEventListener('click', () => {
    document.body.removeChild(modal);
  });

  // Cerrar al hacer clic fuera del modal
  modal.addEventListener('click', (e) => {
    if (e.target === modal) {
      document.body.removeChild(modal);
    }
  });
};

export const isContactCenterOpen = () => {
  return true; // Demo mode
};

export const getWeekDates = () => {
  const today = new Date();
  const currentDay = today.getDay();
  const monday = new Date(today);
  monday.setDate(today.getDate() - currentDay + 1);

  const weekDates = [];
  for (let i = 0; i < 7; i++) {
    const date = new Date(monday);
    date.setDate(monday.getDate() + i);
    weekDates.push(date);
  }
  return weekDates;
};

export const formatDateWithDay = (day: string, date: Date) => {
  const dayName = formatDayName(day);
  const dateStr = date.toLocaleDateString('es-HN', {
    day: '2-digit',
    month: '2-digit',
  });
  return `${dayName}`;

  // TODO: Para una Segunda Versión
  return `${dayName} ${dateStr}`;
};

export const formatDayName = (day: string) => {
  const days: { [key: string]: string } = {
    monday: 'Lunes',
    tuesday: 'Martes',
    wednesday: 'Miércoles',
    thursday: 'Jueves',
    friday: 'Viernes',
    saturday: 'Sábado',
    sunday: 'Domingo',
  };
  return days[day] || day;
};
